import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ExpandMore,
  Person,
  Phone,
  School,
  HowToVote,
  Help,
  Info,
  CheckCircle,
  Group,
  TrendingUp,
} from "@mui/icons-material";

interface FAQItem {
  question: string;
  answer: string;
  icon: React.ReactNode;
  color: string;
}

const faqData: FAQItem[] = [
  {
    question: "What is the SK Youth Profiling Form?",
    answer: "The SK Youth Profiling Form is a comprehensive database system designed to collect and organize information about youth residents in Guiwan. It helps the Sangguniang Kabataan (SK) understand the demographics, needs, and capabilities of the youth population to better plan and implement youth-focused programs and services.",
    icon: <Info />,
    color: "#1B75BC",
  },
  {
    question: "Who should fill out this form?",
    answer: "All youth residents of Guiwan aged 15-30 years old are encouraged to register. Whether you're a student, working professional, unemployed, or in any other status, your information is valuable for us to create targeted programs that serve your specific needs.",
    icon: <Person />,
    color: "#F15A29",
  },
  {
    question: "What information is collected?",
    answer: "We collect basic personal information (name, age, contact details), educational background, work status, and youth classification (NEET, Working Youth, Youth with Special Needs, PWD, Indigenous People). We also track SK participation such as voter registration and assembly attendance to measure youth engagement.",
    icon: <School />,
    color: "#FDB813",
  },
  {
    question: "How is my information used?",
    answer: "Your information is used solely for planning and implementing SK programs. We use this data to identify skills gaps, create targeted employment programs, organize educational workshops, and develop initiatives that address the specific needs of different youth groups in our barangay. Your data is secure and never shared with third parties.",
    icon: <Group />,
    color: "#262262",
  },
  {
    question: "Is my data secure?",
    answer: "Yes! We take data privacy seriously. All information is stored in a secure database with strict access controls. Only authorized SK officials can access the data, and we comply with data protection regulations. We also track device and browser information solely for security and analytics purposes.",
    icon: <CheckCircle />,
    color: "#059669",
  },
  {
    question: "What programs can I join after registering?",
    answer: "After registering, you'll be eligible for various SK programs including: skills training workshops, job fairs, youth camps, leadership seminars, sports activities, and special programs for NEET youth (Not in Education, Employment, or Training). We also organize events specifically for Working Youth, PWDs, and Indigenous People youth.",
    icon: <TrendingUp />,
    color: "#7C3AED",
  },
  {
    question: "Do I need to be a registered voter?",
    answer: "No, voter registration is not required to join SK programs. However, we do track voter registration status to understand civic engagement levels among youth. If you are a registered voter, we also note whether you participated in the last election to measure youth political participation.",
    icon: <HowToVote />,
    color: "#DC2626",
  },
  {
    question: "Can I update my information later?",
    answer: "Currently, you can re-register if your contact information changes. In the future, we plan to implement a profile update feature. For now, if you need to update critical information, please contact the SK office directly or submit a new registration with updated details.",
    icon: <Phone />,
    color: "#0891B2",
  },
];

const benefits = [
  {
    title: "Better Program Planning",
    description: "SK uses your data to design programs that match actual youth needs",
    color: "#1B75BC",
  },
  {
    title: "Targeted Opportunities",
    description: "Get notified about job fairs, training, and events relevant to you",
    color: "#F15A29",
  },
  {
    title: "Community Connection",
    description: "Connect with other youth in your zone and build your network",
    color: "#FDB813",
  },
  {
    title: "Voice in Governance",
    description: "Your participation helps shape youth policies in Guiwan",
    color: "#262262",
  },
];

function About() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const scrollToForm = () => {
    const element = document.getElementById('youth-profiling-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>

      {/* Benefits Section */}
      <Typography
        variant="h4"
        component="h2"
        fontWeight="bold"
        sx={{
          mb: { xs: 2, md: 3 },
          textAlign: 'center',
          color: 'text.primary',
          fontSize: { xs: '1.5rem', md: '2rem' },
        }}
      >
        Why Register?
      </Typography>

      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
        {benefits.map((benefit) => (
          <Grid item xs={12} sm={6} md={3} key={benefit.title}>
            <Card
              elevation={isDarkMode ? 8 : 3}
              sx={{
                height: '100%',
                borderRadius: { xs: 2, md: 3 },
                borderLeft: `4px solid ${benefit.color}`,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'background.paper',
                '&:hover': {
                  transform: { xs: 'none', md: 'translateY(-4px)' },
                  boxShadow: isDarkMode
                    ? '0 8px 25px rgba(0,0,0,0.4)'
                    : '0 8px 25px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box
                  sx={{
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 },
                    borderRadius: 2,
                    backgroundColor: `${benefit.color}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <TrendingUp sx={{ color: benefit.color, fontSize: { xs: 20, md: 24 } }} />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    mb: 1,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', md: '1.1rem' },
                  }}
                >
                  {benefit.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                  }}
                >
                  {benefit.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* FAQ Section */}
      <Paper
        id="faq-section"
        className="faq-section"
        elevation={isDarkMode ? 8 : 3}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: { xs: 2, md: 4 },
          border: '1px solid',
          borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'grey.200',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'background.paper',
        }}
      >
        <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 48, md: 64 },
              height: { xs: 48, md: 64 },
              borderRadius: { xs: 2, md: 3 },
              background: 'linear-gradient(135deg, #1B75BC 0%, #F15A29 100%)',
              mb: 2,
            }}
          >
            <Help sx={{ fontSize: { xs: 24, md: 32 }, color: 'white' }} />
          </Box>
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            sx={{
              color: 'text.primary',
              mb: 1,
              fontSize: { xs: '1.25rem', md: '1.75rem' },
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '0.85rem', md: '1rem' },
              px: { xs: 1, md: 0 },
            }}
          >
            Learn more about the SK Youth Profiling System and how it helps our community
          </Typography>
        </Box>

        {faqData.map((faq) => (
          <Accordion
            key={faq.question}
            expanded={expanded === `panel${faq.question}`}
            onChange={handleChange(`panel${faq.question}`)}
            sx={{
              mb: 1.5,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: isDarkMode
                ? '0 2px 8px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.06)',
              '&:before': { display: 'none' },
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'background.paper',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'grey.100',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ color: faq.color }} />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 2,
                },
                px: { xs: 2, md: 3 },
                py: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 32, md: 40 },
                  height: { xs: 32, md: 40 },
                  borderRadius: 2,
                  backgroundColor: `${faq.color}15`,
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(faq.icon as React.ReactElement, {
                  sx: { color: faq.color, fontSize: { xs: 16, md: 20 } },
                })}
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  pr: 1,
                }}
              >
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, pt: 0 }}>
              <Box sx={{ pl: { xs: 6, md: 7 } }}>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    fontSize: { xs: '0.85rem', md: '0.875rem' },
                  }}
                >
                  {faq.answer}
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        <Box sx={{ mt: { xs: 3, md: 4 }, textAlign: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
              fontSize: { xs: '0.8rem', md: '0.875rem' },
            }}
          >
            Still have questions? Contact us at the SK Office or through our social media channels.
          </Typography>
          <Button
            variant="outlined"
            size={isMobile ? 'medium' : 'large'}
            onClick={scrollToForm}
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              fontWeight: 'bold',
              px: { xs: 3, md: 4 },
              borderRadius: 3,
              minHeight: isMobile ? 44 : 'auto',
              '&:hover': {
                backgroundColor: 'rgba(27, 117, 188, 0.08)',
                borderColor: 'primary.main',
              },
            }}
          >
            Back to Registration Form
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default About;
